// FAQ content per calculator — used for FAQPage structured data + visible FAQ block.
// Keep answers conversational, accurate, and ~40-80 words each.
// These boost page word count (AdSense prefers 300+ words/page) and earn rich
// snippet eligibility.

export const FAQS = {
    mortgage: [
        {
            q: "How is my monthly mortgage payment calculated?",
            a: "The monthly payment is based on your loan amount (home price minus down payment), interest rate, and loan term using the standard amortization formula. Our calculator also adds 1/12 of your annual property tax and home insurance to the base principal + interest figure so you see the real monthly outlay.",
        },
        {
            q: "Does this include PMI?",
            a: "No. Private Mortgage Insurance is not included. If your down payment is below 20%, expect to pay roughly 0.5%–1.5% of your loan amount per year in PMI until you reach 20% equity. Add that to the monthly result manually for a full picture.",
        },
        {
            q: "What's a reasonable mortgage payment relative to my income?",
            a: "The common rule: total housing cost (principal, interest, taxes, insurance) should stay under 28% of your gross monthly income. Total debt payments combined should stay under 36%. Lenders call these the 28/36 ratios.",
        },
        {
            q: "How does extra principal payment affect my loan?",
            a: "Every extra dollar goes straight against principal, which compounds over time. On a $400K, 30-year loan at 6.5%, paying just $200 extra a month shaves roughly 6 years off the term and saves over $130K in interest.",
        },
    ],
    loan: [
        {
            q: "What's the difference between APR and interest rate?",
            a: "The interest rate is what's applied to your principal. APR (Annual Percentage Rate) bakes in origination fees and certain other charges, giving you a more honest cost of borrowing. Two loans with the same interest rate can have very different APRs.",
        },
        {
            q: "Is a shorter loan term always better?",
            a: "Shorter terms mean higher monthly payments but dramatically less total interest. Cutting a 5-year loan to 3 years typically increases the payment ~60% but slashes total interest by about 40%. Choose the shortest term you can comfortably afford.",
        },
        {
            q: "Can I pay off a personal loan early?",
            a: "Most personal loans allow prepayment, but some charge a prepayment penalty — always read the fine print. When allowed, paying extra against principal aggressively reduces total interest because the remaining balance accrues less each month.",
        },
    ],
    "auto-loan": [
        {
            q: "What is the 20/4/10 rule for buying a car?",
            a: "Put at least 20% down, finance no longer than 4 years, and keep total transportation costs (loan payment, insurance, fuel, maintenance) under 10% of gross income. Cars built to this rule rarely become financial burdens.",
        },
        {
            q: "Should I finance through the dealer or my bank?",
            a: "Shop both, and get pre-approved from your bank or credit union before visiting the dealer. Pre-approval gives you a rate to beat and negotiating leverage — dealers will sometimes match or undercut just to keep the financing.",
        },
        {
            q: "Is a longer car loan worth the lower monthly payment?",
            a: "Usually not. A 72- or 84-month loan lowers the payment but often leaves you underwater (owing more than the car is worth) for years. If you can't afford the car on a 48-month loan, you probably can't afford the car.",
        },
    ],
    refinance: [
        {
            q: "When is refinancing actually worth it?",
            a: "The simple rule: calculate your break-even point (closing costs ÷ monthly savings). If you plan to stay in the home longer than that, refinancing makes sense. If you'll move before break-even, you lose money on paper.",
        },
        {
            q: "How much should I expect to pay in closing costs?",
            a: "Typical refinance closing costs run 2%–5% of the loan amount, covering appraisal, title, origination, and other fees. Some lenders offer 'no-closing-cost' refis — but the cost is usually baked into a slightly higher interest rate.",
        },
        {
            q: "Does refinancing hurt my credit score?",
            a: "A refinance triggers a hard credit inquiry, which may shave a few points temporarily. The bigger long-term impact is neutral to positive — paying a mortgage on time continues to build credit, regardless of refi.",
        },
    ],
    "compound-interest": [
        {
            q: "Why is compound interest so powerful over long periods?",
            a: "Because the returns on your returns themselves earn returns. Over a few years compounding looks linear; over 20+ years it explodes. $10,000 at 8% grows to $46,610 in 20 years but $217,245 in 40 years — the second half does all the work.",
        },
        {
            q: "Is a 7%–10% return realistic?",
            a: "The long-term (100+ year) average of the US stock market is around 10% nominal, 7% after inflation. Individual years are wildly variable — some lose 30%, others gain 30% — but long holding periods consistently smooth toward the average.",
        },
        {
            q: "Should I account for inflation?",
            a: "Yes, if you're planning in terms of real purchasing power. To model real returns, use your expected nominal return minus expected inflation (e.g., 7% instead of 10%). Our calculator shows nominal growth — subtract ~3% mentally for real purchasing power.",
        },
    ],
    retirement: [
        {
            q: "What is the 4% rule?",
            a: "It's a classic retirement-safe-withdrawal guideline: in your first year of retirement, withdraw 4% of your portfolio; each subsequent year, adjust that dollar amount for inflation. Based on historical data, a 4% withdrawal rate has a ~95% success rate over 30-year retirements.",
        },
        {
            q: "Should I always contribute enough to get the full employer 401(k) match?",
            a: "Almost always yes. An employer match is an instant 50%–100% return on your contribution — nothing else in finance comes close. The only exception is if you have extremely high-interest debt that you're actively paying down.",
        },
        {
            q: "What's the difference between a Traditional and Roth 401(k)?",
            a: "Traditional: contributions reduce your taxable income today, but withdrawals in retirement are taxed. Roth: you pay tax on contributions now, but withdrawals are tax-free. Rule of thumb: Roth wins if you expect higher tax rates later, Traditional wins if lower.",
        },
        {
            q: "Is it too late to start saving at 45?",
            a: "Not at all. Starting at 45 with $100K and contributing $1,500/month at 7% return gets you to roughly $1.1M by 65. The math is less forgiving than starting at 25, but 20 years is still long enough for serious compounding.",
        },
    ],
    "investment-roi": [
        {
            q: "Why does CAGR matter more than total return?",
            a: "CAGR (annualized return) makes investments of different durations comparable. A 45% total return is great over 2 years (20.4% CAGR) and underwhelming over 10 years (3.8% CAGR). Always compare annualized, not total.",
        },
        {
            q: "Does this calculator factor in taxes or fees?",
            a: "No. It shows gross return on your principal. For a true net figure, subtract transaction fees, management fees (expense ratios), and capital gains taxes (long-term 15%–20%, short-term at your income rate).",
        },
        {
            q: "How do I calculate ROI with multiple deposits over time?",
            a: "A simple CAGR calculation isn't right for recurring deposits — you need IRR (Internal Rate of Return), which handles cash flows at different times. For recurring investing, our Compound Interest calculator is the better tool.",
        },
    ],
    "debt-payoff": [
        {
            q: "Should I use the Avalanche or Snowball method?",
            a: "Avalanche (highest APR first) saves the most money mathematically. Snowball (smallest balance first) produces faster wins that keep people motivated. Behavioural economists often favour Snowball — because the plan you actually stick with beats the mathematically perfect plan you don't.",
        },
        {
            q: "Does paying off debt improve my credit score?",
            a: "Usually yes, especially for credit cards. Lenders look at credit utilization (balance ÷ limit). Dropping utilization from 60% to under 30% can boost your score by 30–70 points in one billing cycle.",
        },
        {
            q: "Should I invest or pay off debt first?",
            a: "Rough rule: pay off anything above ~8% APR before investing outside of an employer 401(k) match. Below that, you can reasonably split — the long-term stock market return averages 7%–10%, so the math is close.",
        },
    ],
    "credit-card-payoff": [
        {
            q: "Why are minimum payments a trap?",
            a: "Card minimums are designed to barely cover interest. A $5,000 balance at 22% APR on minimums can take 20+ years to pay off and cost more in interest than the original debt. Always pay more than the minimum.",
        },
        {
            q: "Should I do a balance transfer to a 0% APR card?",
            a: "Often yes — but only if you have a real plan to pay it off within the 0% window (typically 12–21 months). Most transfers charge 3%–5% up front, so math it out first. And never run up the original card again after transfer.",
        },
        {
            q: "Is it better to pay one big card or split across several?",
            a: "Depends. For credit-score optimization, reducing utilization across multiple cards helps. For interest savings, dump everything against the highest APR. If the scores are close in APR, paying down whichever has the highest utilization % gives the best score bump.",
        },
    ],
    "savings-goal": [
        {
            q: "Where should I park short-term savings?",
            a: "For goals within 5 years: high-yield savings accounts (4%–5% APY as of 2026) or short-term CDs. For goals beyond 5 years, broadly diversified index funds historically outperform — but with real volatility. Don't put next year's down payment in stocks.",
        },
        {
            q: "How do I stay motivated with long savings horizons?",
            a: "Automate transfers on payday so saving happens before spending. Name your savings accounts after the goal (&lsquo;Hawaii Fund&rsquo;, &lsquo;Kitchen Remodel&rsquo;). Track progress visually. Behavioural tricks matter more than theoretical returns.",
        },
    ],
    "simple-interest": [
        {
            q: "When would a real loan use simple interest instead of compound?",
            a: "Most short-term personal loans, some auto loans, Treasury securities paying coupons, and many classroom homework problems. If a bank quotes you &lsquo;simple 5% per year on $5,000 for 3 years,&rsquo; you know exactly what it means: $750 in interest, period.",
        },
        {
            q: "Is compound interest always worse than simple interest for borrowers?",
            a: "Yes, with one exception: very short-term loans where compounding happens less than annually. Over any meaningful period, compound interest accrues more interest because each period's interest starts earning its own interest.",
        },
    ],
    salary: [
        {
            q: "What's my actual take-home from a salary?",
            a: "A quick rule for the US: subtract 25%–30% from gross for federal tax, state tax, and FICA payroll taxes. Actual take-home varies widely by state (Texas/Florida higher; California/New York lower) and filing status. Our Income Tax calculator gives a more precise federal number.",
        },
        {
            q: "How many hours per year is a standard full-time job?",
            a: "The traditional answer is 2,080 hours (40 × 52). If you take two weeks of vacation, drop to ~2,000. Converting $90,000/year to hourly: divide by 2,000 for &lsquo;effective&rsquo; hourly rate of $45/hour.",
        },
        {
            q: "When negotiating, should I focus on salary or total comp?",
            a: "Total comp. Base salary is usually the biggest lever, but bonus, equity, 401(k) match, healthcare, and PTO can easily swing the real number by $20K–$50K. Model the full package before comparing offers.",
        },
    ],
    tip: [
        {
            q: "What's the standard tip in the US?",
            a: "15% is the historical baseline. 18%–20% is typical for sit-down restaurant service. 20%–25% is standard for exceptional service or bartenders. Takeout is usually 0%–10%. Delivery drivers typically get 15%–20%.",
        },
        {
            q: "Do I tip on the pre-tax or post-tax total?",
            a: "Technically, on the pre-tax subtotal. Practically, most people tip on the full bill — and the difference is small. If you want to be precise, our calculator works fine on pre-tax or post-tax; you pick.",
        },
        {
            q: "How much do I tip outside the US?",
            a: "Wildly varies. Japan: don't tip (it can be offensive). Most of Europe: 5%–10% or rounding up. Australia/NZ: usually not expected. Always check the menu — many countries build service charges into the bill.",
        },
    ],
    "income-tax": [
        {
            q: "Why is my effective tax rate lower than my marginal rate?",
            a: "The US uses progressive brackets — only income above each threshold is taxed at that bracket's rate. Your &lsquo;marginal&rsquo; rate is your top bracket. Your &lsquo;effective&rsquo; rate is total tax ÷ total income, which blends all brackets and is always lower than your top rate.",
        },
        {
            q: "What does this calculator not include?",
            a: "State income tax (varies from 0% in Texas/Florida to 13%+ in California), FICA payroll taxes (7.65%), Medicare surtax for high earners, credits (child tax credit, EITC), and itemized deductions. For precise numbers, use the IRS Tax Withholding Estimator.",
        },
        {
            q: "Should I take the standard deduction or itemize?",
            a: "Take whichever is bigger. For most US filers, the standard deduction ($14,600 single / $29,200 married joint for 2024) beats itemizing. You'd typically need a large mortgage interest payment, significant charitable giving, or high state/local taxes to benefit from itemizing.",
        },
    ],
    "rent-vs-buy": [
        {
            q: "Is renting always throwing money away?",
            a: "No. Renters pay less on maintenance, property tax, HOA, closing/selling costs, and have mobility that has real financial value. The honest comparison tracks net wealth under both paths — including what you could have earned investing the down payment instead.",
        },
        {
            q: "What's the breakeven time for buying vs renting?",
            a: "Typically 5–7 years in stable markets, longer in expensive coastal markets. If you might move within 5 years, renting is usually the better financial bet — closing costs and transaction friction eat too much of the appreciation.",
        },
        {
            q: "How sensitive is the answer to home appreciation assumptions?",
            a: "Very. At 3% appreciation, buying often wins over 10+ years. At 1%, it usually doesn't. Historical US home appreciation is ~3.5% nominal (~1% real after inflation). Don't assume the last decade's 5%–8% home gains will continue forever.",
        },
    ],
    "va-loan": [
        {
            q: "Who qualifies for a VA loan?",
            a: "Active-duty service members, veterans with qualifying service, and many surviving spouses. You'll need a Certificate of Eligibility (COE) from the VA. Eligibility is binary — once you qualify, you keep the benefit for life (and can reuse it).",
        },
        {
            q: "What is the VA funding fee?",
            a: "A one-time fee paid to the VA in place of monthly mortgage insurance. For a 0%-down first-time use, it's 2.15% of the loan. Subsequent uses are 3.3%. Putting 5%+ down lowers it; disabled veterans are exempt entirely. The fee can be rolled into the loan.",
        },
        {
            q: "Does VA loan require PMI?",
            a: "No. That's the single biggest monthly advantage — on a $400K loan at 6.5%, skipping PMI saves roughly $150–$200 per month vs a low-down-payment conventional or FHA loan.",
        },
        {
            q: "Can I buy any home with a VA loan?",
            a: "The home must be your primary residence and pass a VA appraisal that checks for safety and habitability (the 'Minimum Property Requirements'). Investment properties and vacation homes aren't eligible. County loan limits apply above certain high-cost thresholds.",
        },
    ],
    "fha-loan": [
        {
            q: "What's the minimum down payment on an FHA loan?",
            a: "3.5% if your credit score is 580 or higher. Between 500–579 you can still qualify, but with 10% down. Below 500, FHA won't back the loan. The 3.5% minimum is the main reason FHA dominates first-time-buyer loans.",
        },
        {
            q: "What is MIP and how long does it last?",
            a: "Mortgage Insurance Premium. You pay 1.75% upfront (UFMIP — typically financed into the loan) plus an annual MIP of roughly 0.55% spread across 12 monthly payments. For loans with under 10% down, MIP lasts the entire loan life — the main drawback vs conventional loans.",
        },
        {
            q: "What are FHA loan limits?",
            a: "The 2024 FHA baseline limit is $498,257 for a single-family home in most counties, going up to $1,149,825 in high-cost areas. Any amount above your county's limit pushes you into conforming or jumbo territory.",
        },
        {
            q: "When should I use FHA vs conventional?",
            a: "FHA wins when your credit score is 580–680 or your down payment is below 5%. Conventional typically wins above 680 credit and 10%+ down because conventional PMI is cheaper and drops off at 20% equity, while FHA MIP often lasts the life of the loan.",
        },
    ],
    "jumbo-loan": [
        {
            q: "What makes a loan 'jumbo'?",
            a: "Any mortgage above the annual Federal Housing Finance Agency conforming loan limit. For 2024 that's $766,550 in most US counties and up to $1,149,825 in high-cost areas. Above those lines the loan can't be sold to Fannie/Freddie, making it a non-conforming 'jumbo' loan.",
        },
        {
            q: "Are jumbo loan rates higher?",
            a: "Usually by 0.1–0.5 percentage points versus conforming loans, though the gap has narrowed in recent years and sometimes even inverted. Since lenders hold jumbo loans on their own books (or package them into private MBS), they charge a risk premium.",
        },
        {
            q: "What down payment do jumbo lenders require?",
            a: "Typically 10%–20% down, with 20% being the most common sweet spot for avoiding PMI-like surcharges. Some premium programs go as low as 10% for very strong credit (760+) and high reserves.",
        },
        {
            q: "What extra underwriting applies to jumbo loans?",
            a: "Expect stricter DTI (debt-to-income) limits — often capped at 43% or lower — plus higher cash reserves (6–12 months of payments in liquid assets). Two full appraisals are common above $1M. Credit score minimums typically start at 700, sometimes 720+.",
        },
    ],
    heloc: [
        {
            q: "How does the draw period work?",
            a: "Draw period is the window (typically 10 years) when you can withdraw funds from your line and pay only the interest on what's been drawn. It functions like a credit card secured by your home — flexible access, variable rate, low minimums.",
        },
        {
            q: "What happens after the draw period ends?",
            a: "The loan converts to a repayment period (typically 20 years) where the outstanding balance amortizes into fixed principal + interest payments. The shock of that transition can roughly double your payment, so plan for it well in advance.",
        },
        {
            q: "How is HELOC interest calculated?",
            a: "Variable rate tied to the Prime Rate plus a lender margin. If Prime is 8.5% and your margin is 0.5%, your rate is 9%. Rate adjusts with Prime — meaning payments rise when the Fed raises rates. Read the cap/floor terms carefully.",
        },
        {
            q: "How much can I borrow with a HELOC?",
            a: "Most lenders cap the Combined Loan-To-Value (CLTV) — existing mortgage + HELOC limit — at 80%–85% of your home's appraised value. So on a $600K home with a $400K mortgage, you could typically qualify for up to $80K–$110K of line.",
        },
    ],
    "overtime-tips": [
        {
            q: "What is the 'No Tax on Overtime' deduction?",
            a: "A federal above-the-line deduction introduced in the One Big Beautiful Bill Act (OBBBA), signed in July 2025. It lets hourly workers deduct up to $12,500 (single) or $25,000 (married filing jointly) of qualifying overtime premium pay from their taxable income for tax years 2025–2028. Only the 'half' portion of time-and-a-half qualifies — not the full overtime wage.",
        },
        {
            q: "What counts as the 'premium' portion of overtime?",
            a: "If you earn $20/hour regular and $30/hour in overtime (time-and-a-half), only the extra $10/hour premium over your regular rate qualifies for the deduction. On a $20/hour regular rate with 10 overtime hours per week for 50 weeks, that's $10 × 10 × 50 = $5,000 of deductible overtime premium.",
        },
        {
            q: "What is the 'No Tax on Tips' deduction?",
            a: "A matching OBBBA deduction of up to $25,000 of qualifying tip income per return, for tax years 2025+. It covers cash tips, charged tips, and tip-sharing for occupations that 'customarily and regularly' received tips before December 31, 2024 — waitstaff, bartenders, hairstylists, delivery drivers, and similar roles.",
        },
        {
            q: "Do I still owe Social Security and Medicare on OT and tips?",
            a: "Yes. The OBBBA deductions only reduce your federal INCOME tax — FICA payroll taxes (7.65% for Social Security + Medicare) still apply to all your wages, including overtime premium and tips. State income taxes may also still apply depending on how your state conforms to federal tax law.",
        },
        {
            q: "Are there income limits?",
            a: "Yes. Both deductions begin phasing out at $150,000 MAGI for single filers ($300,000 married filing jointly). The overtime deduction reduces by $100 for every $1,000 of MAGI above the threshold, fully phasing out at $275,000 single / $550,000 joint. High earners get little to no benefit.",
        },
        {
            q: "Do I have to itemize to claim these?",
            a: "No — these are 'above-the-line' deductions, meaning you can claim them whether you take the standard deduction or itemize. They reduce taxable income directly, making them accessible to nearly every tipped or overtime worker.",
        },
    ],
    "student-loan": [
        {
            q: "Should I refinance my federal student loans into private?",
            a: "Usually no. Federal loans come with income-driven repayment plans, deferment options, and Public Service Loan Forgiveness (PSLF) eligibility — all of which you lose if you refinance to a private lender. Refinancing only makes sense if you have stable high income, no PSLF aspirations, and a private quote that beats your federal rate by 1.5%+ after fees.",
        },
        {
            q: "What's the average student loan payoff timeline?",
            a: "Standard federal repayment is 10 years. Borrowers with $30,000+ qualify for extended terms up to 25 years (lower payment, much higher total interest). Income-driven plans (SAVE/PAYE/IBR) cap payments at 5–20% of discretionary income for 20–25 years with potential forgiveness on the remaining balance.",
        },
        {
            q: "Does paying extra on my highest-interest loan help?",
            a: "Yes — significantly. If you have multiple loans, target the highest-rate loan first (avalanche method). On a $40,000 balance at 6.8% over 10 years, an extra $150/month shaves roughly 3 years off the term and saves about $5,800 in interest.",
        },
        {
            q: "Can I deduct student loan interest?",
            a: "Yes. Up to $2,500 of student loan interest paid is deductible as an above-the-line deduction (you don't have to itemize). The deduction phases out between $80,000–$95,000 MAGI single ($165,000–$195,000 joint) — high earners get reduced or no benefit.",
        },
    ],
    "roth-vs-traditional": [
        {
            q: "When does Roth win?",
            a: "Roth wins when your tax rate in retirement is HIGHER than your tax rate today. That's typical for: young professionals at the start of their career, folks who expect tax rates to rise generally, and anyone with a long horizon (30+ years) where tax-free compounding wins by default.",
        },
        {
            q: "When does Traditional win?",
            a: "Traditional 401(k) wins when your tax rate today is HIGHER than your retirement rate. This applies to: high earners in their peak earning years, anyone expecting to retire to a no-income-tax state, and households planning a frugal retirement that drops them into a lower bracket.",
        },
        {
            q: "What about the employer match?",
            a: "Employer match goes into the Traditional bucket regardless of your contribution choice (this is federal law, not employer policy). So if you contribute Roth, your match is still Traditional and grows tax-deferred — you'll have both buckets at retirement, which is actually a feature for tax-bracket flexibility.",
        },
        {
            q: "Is there an income limit?",
            a: "Roth IRA has direct-contribution income phaseouts ($150K–$165K single, $236K–$246K joint in 2025). Roth 401(k) has NO income limit — you can earn $1M and still contribute up to the $23,500 401(k) limit as Roth if your plan offers it. The 'backdoor Roth' route works around the IRA limits for high earners.",
        },
    ],
    "emergency-fund": [
        {
            q: "How many months of expenses should I save?",
            a: "The classic rule is 3–6 months. The right number depends on job stability: dual-income households with stable employers can stay at 3 months; single-income families and self-employed people should target 6–12 months. The calculator above factors job stability and household risk into a more personalized recommendation.",
        },
        {
            q: "Should an emergency fund be in a regular savings account?",
            a: "Use a high-yield savings account (HYSA), money market account, or short-term Treasury fund. Currently 4–5% APY is available at most online banks (Marcus, Ally, Capital One 360, etc.). Avoid: checking accounts (no yield), CDs (early withdrawal penalty defeats the purpose), and the stock market (could drop 30% the same week you need it).",
        },
        {
            q: "What counts as an emergency?",
            a: "Job loss, major medical event, urgent home repair (roof, HVAC, plumbing), urgent car repair (transmission, engine), or family crisis requiring travel. Vacations, holidays, predictable car maintenance, and 'great deals' are NOT emergencies — those need their own sinking funds.",
        },
        {
            q: "Should I build an emergency fund before paying off debt?",
            a: "Yes — at least a $1,000–$2,000 starter buffer. Without one, the next car breakdown becomes a credit card balance, undoing your debt-payoff progress. Build the starter fund, attack high-interest debt, then complete the 3–6 month full fund. This is Dave Ramsey's Baby Step framework, and the math holds up.",
        },
    ],
};
